import React, { useEffect, useRef, useState } from "react";
import {
  CameraFill,
  ChevronLeft,
  Person,
  PlusLg,
  Trash,
} from "react-bootstrap-icons";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import { useNavigate, useParams } from "react-router-dom";
import { decryptData, encryptData } from "../../utils/crypto";
import Utils from "../../utils";
import { useTranslation } from "react-i18next";
import {
  adminUpdateProfile,
  getProfileDetails,
} from "../../services/authServices";
import CONFIG from "../../config";
import { notify } from "../../components/Common/Alerts";

const AdminProfile = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const inputRef = useRef(null);
  const { t } = useTranslation();
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem("theme") === "dark",
  );

  const [formData, setFormData] = useState({
    full_name: "",
    mobile: "",
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({
    full_name: "",
    mobile: "",
    email: "",
  });
  const [btnLoading, setBtnLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [profileDetails, setProfileDetails] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const handleStorageChange = () => {
      setDarkMode(localStorage.getItem("theme") === "dark");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];

      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setFile(null);
    setPreview(null);
  };

  const validateField = (name, value) => {
    switch (name) {
      case "full_name":
        if (!value) return t("nameIsRequired");
        return "";

      case "mobile":
        if (!value) return t("mobileIsRequired");
        const mobileRegex = /^\d{10}$/;
        if (!mobileRegex.test(value)) {
          return t("enterDigitMobileNumber");
        }
        return "";

      case "email":
        if (!value?.trim()) return "Email is required";
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(value)) return t("pleaseEnterValidEmail");
        return "";

      // case "password":
      //   if (value.length < 6)
      //     return "Password must be at least 6 characters long";
      //   if (!/[a-zA-Z]/.test(value))
      //     return "Password must contain at least one letter";
      //   return "";

      // case "confirm_password":
      //   if (!value) return t("confirmPasswordIsRequired");
      //   if (value !== formData.password) return t("passwordsDoNotMatch");

      //   return "";

      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (value && !emailRegex.test(value)) {
          return t("pleaseEnterValidEmail");
        }
        return "";

      default:
        return "";
    }
  };

  const handleMobileValidation = () => {
    let errorMsg = "";

    const mobileRegex = /^\d{10}$/;

    if (!formData.mobile) {
      errorMsg = "Mobile is required";
    } else if (!mobileRegex.test(formData.mobile)) {
      errorMsg = "Enter a valid 10-digit mobile number";
    }

    setErrors((prev) => ({
      ...prev,
      mobile: errorMsg,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobile") {
      const cleanedValue = value.replace(/\D/g, "").slice(0, 10);

      setFormData((prev) => ({
        ...prev,
        mobile: cleanedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  useEffect(function () {
    getProfileDetails()
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        setProfileDetails(decrypted.data);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  useEffect(
    function () {
      if (profileDetails) {
        setFormData({
          full_name: profileDetails?.full_name,
          mobile: profileDetails?.mobile,
          email: profileDetails?.email,
        });
        setFile(profileDetails?.profile_image);
        setPreview(profileDetails?.profile_image);
      }
    },
    [profileDetails],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      full_name: validateField("full_name", formData.full_name),
      mobile: validateField("mobile", formData.mobile),
      email: validateField("email", formData.email),
    };
    Utils.focusError();
    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).some((err) => err);

    if (hasErrors) return;

    setBtnLoading(true);

    let data = {
      full_name: formData.full_name?.trim() || "",
      mobile: formData.mobile || "",
      email: formData.email?.trim() || "",
      password: formData.password || "",
      is_delete_icon: file ? 0 : 1,
    };

    let q = encryptData(data);

    const formPayload = new FormData();

    if (file) {
      formPayload.append("files", file);
    }
    formPayload.append("q", q);

    try {
      const response = await adminUpdateProfile(formPayload);
      const decrypted = decryptData(response.data.data);
      notify.success(decrypted.message);
      const storedData = JSON.parse(localStorage.getItem(CONFIG.login_data));

      const updatedData = {
        ...storedData,
        user: {
          ...storedData.user,
          full_name: decrypted?.data?.full_name,
          mobile: decrypted?.data?.mobile,
          email: decrypted?.data?.email,
        },
      };
      localStorage.setItem(CONFIG.login_data, JSON.stringify(updatedData));

      setErrors("");
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <div>
      <div className="mx-auto space-y-8 animate-fade-up pb-12">
        <div className="flex items-center gap-4 md:gap-6 flex-shrink-0 px-2 mb-2">
          <button
            onClick={() => navigate(-1)}
            className="md:hidden group flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:px-4 md:py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all active:scale-95 shadow-sm"
          >
            <ChevronLeft
              size={18}
              className="group-hover:-translate-x-1 transition-transform md:hidden"
            />
            <span className="hidden ml-2 font-bold text-sm">{t("back")}</span>
          </button>

          <div className="flex-1">
            <h2 className="text-xl md:text-3xl font-black text-slate-800 dark:text-white tracking-tight leading-tight">
              {t("updateProfile")}{" "}
            </h2>
          </div>
        </div>
        <section className="bg-white dark:bg-slate-900 rounded-[40px] border border-gray-100 p-8 lg:p-12 shadow-sm">
          <div className="flex items-center gap-4 mb-10">
            <div className="w-12 h-12 bg-brand-orange/10 dark:bg-brand-orange/20 text-brand-orange dark:text-brand-orange rounded-2xl flex items-center justify-center">
              <Person size={24} />
            </div>
            <h2 className="text-xl font-black text-slate-800 dark:text-slate-100">
              {t("profileDetails")}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            <div className="lg:col-span-1 flex flex-col items-center">
              <div
                className="relative group cursor-pointer"
                onClick={() => inputRef.current.click()}
              >
                <input
                  type="file"
                  ref={inputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  className="hidden"
                />

                <div className="w-36 h-36 bg-gray-50 rounded-full border-4 border-white shadow-xl flex items-center justify-center overflow-hidden transition-transform group-hover:scale-105">
                  {preview ? (
                    <img
                      src={preview}
                      alt="preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <CameraFill size={32} className="text-gray-200" />
                  )}
                </div>

                {!file && (
                  <div className="absolute bottom-2 right-2 bg-brand-orange text-white p-2.5 rounded-full border-4 border-white shadow-lg">
                    <PlusLg size={16} />
                  </div>
                )}

                {file && (
                  <div
                    onClick={handleDelete}
                    className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full border-4 border-white shadow-lg"
                  >
                    <Trash size={14} />
                  </div>
                )}
              </div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-6">
                {t("profilePicture")}
              </p>
            </div>

            <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <label htmlFor="name" className="input-label">
                  {t("fullName")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="full_name"
                  placeholder={t("enterYourOwnerName")}
                  className={`input-field ${errors.full_name ? "input-error" : ""}`}
                  value={formData?.full_name}
                  onChange={handleChange}
                  maxLength={30}
                />
                {errors.full_name && (
                  <span className="text-danger">{errors.full_name}</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="name" className="input-label">
                  {t("mobileNumber")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder={t("enterYourMobileNumber")}
                  className={`input-field ${errors.mobile ? "input-error" : ""}`}
                  value={formData?.mobile}
                  onChange={handleChange}
                  onKeyDown={(e) => {
                    if (["-", "e", "E", "+"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (["e", "E", "+", "-", "."].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onBlur={handleMobileValidation}
                  name="mobile"
                  min={0}
                />
                {errors.mobile && (
                  <span className={`text-danger`}>{errors.mobile}</span>
                )}
              </div>

              <div className="flex flex-col gap-2 md:col-span-2">
                <label htmlFor="name" className="input-label">
                  {t("email")}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder={t("enterYourEmail")}
                  className={`input-field ${errors.email ? "input-error" : ""}`}
                  value={formData?.email}
                  onChange={handleChange}
                />
                {errors.email && (
                  <span className={`text-danger`}>{errors.email}</span>
                )}
              </div>
            </div>

            <div className="col-span-full flex items-end justify-end gap-4 mb-20 lg:mb-0">
              {" "}
              <button
                type="button"
                form="category-form"
                className="btn-submit-orange flex items-center gap-2 mb-1"
                onClick={handleSubmit}
                disabled={btnLoading}
              >
                {btnLoading && (
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                )}
                {btnLoading ? t("submitting") : t("update")}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminProfile;
